// City normaliser — the location counterpart to `company.js`.
//
// Same problem T151 solved for companies, one column over. `location.city` is
// whatever the source wrote, and the sources disagree, so the corpus splits one
// place across many rows:
//
//   Copenhagen 439 · København 388 · København K 35 · København V 23 ·
//   København Ø 22 · København S 18 · … 33 spellings, ~985 jobs
//
// A city filter built on the raw field would show "Copenhagen: 439" when the
// answer is ~985, and would list "- United States" (341) as a city.
//
// Every rule below comes from a value that actually occurs in the corpus, not
// from imagination. Returns a canonical DISPLAY name — "København", not
// "kobenhavn" — because the same string has to be both the grouping key and the
// filter label. One function produces it, so every row gets byte-identical
// output and exact matching works.
//
// Pure, idempotent, no I/O.

// Values that are not a city. The `-` prefix is how several ATSes encode a
// region ("- United States", "- California", "-APAC"); a `Locations` suffix is
// Greenhouse's country rollup ("Ireland Locations").
const NOT_A_CITY = new Set([
  'remote', 'homeoffice', 'home office', 'hybrid', 'onsite', 'anywhere', 'job',
  'united states', 'usa', 'us', 'united kingdom', 'uk', 'canada', 'india',
  'australia', 'north america', 'europe', 'emea', 'apac', 'namer',
  'deutschlandweit', 'bavaria', 'us east coast', 'us west coast', 'us central',
]);

// One place, several spellings. Keys are lowercased+folded; values are the
// canonical display form. Only pairs observed in the corpus.
const ALIASES = new Map(Object.entries({
  copenhagen: 'København',
  kobenhavn: 'København',
  munich: 'München',
  munchen: 'München',
  cologne: 'Köln',
  koln: 'Köln',
  warsaw: 'Warszawa',
  warszawa: 'Warszawa',
  milan: 'Milano',
  milano: 'Milano',
  lisbon: 'Lisboa',
  lisboa: 'Lisboa',
  vienna: 'Wien',
  wien: 'Wien',
  zurich: 'Zürich',
  gothenburg: 'Göteborg',
  goteborg: 'Göteborg',
  rome: 'Roma',
  roma: 'Roma',
  nuremberg: 'Nürnberg',
  nurnberg: 'Nürnberg',
  dusseldorf: 'Düsseldorf',
  hanover: 'Hannover',
  hannover: 'Hannover',
  brussels: 'Bruxelles',
  bruxelles: 'Bruxelles',
  prague: 'Praha',
  praha: 'Praha',
  aarhus: 'Aarhus',
  arhus: 'Aarhus',
  'new york city': 'New York',
  nyc: 'New York',
  'new york': 'New York',
  'kgs. lyngby': 'Kongens Lyngby',
  'kgs lyngby': 'Kongens Lyngby',
  'kongens lyngby': 'Kongens Lyngby',
  'frankfurt am main': 'Frankfurt',
  frankfurt: 'Frankfurt',
  reykjavik: 'Reykjavík',
  malmo: 'Malmö',
  orebro: 'Örebro',
  oslo: 'Oslo',
}));

// Danish postal districts are written as a trailing letter group: "København K",
// "Aarhus C", "Odense SØ", "Aalborg Øst", "Viby J". They are neighbourhoods, so
// a city filter must fold them into the city.
const DK_DISTRICT = /\s+(?:[KVSNC]|SV|NV|SØ|NØ|SV|Ø|J|M|Øst|Vest|Nord|Syd)$/iu;

// Trailing site/office decoration bolted onto a city name.
const OFFICE_TAIL = /\s*[-–—]\s*(?:the\s+)?[\w\s.]*\b(?:hq|office|building|campus)\b.*$/iu;
const BARE_TAIL = /\s+(?:hq|office|kreativquartier)$/iu;

// jobindex writes the arrangement into the city: "København Ø og mulighed for
// hjemmearbejde".
const WFH_TAIL = /\s+(?:og|eller)\s+mulighed\s+for\s+hjemmearbejde.*$/iu;

// Several sources hand back a list where a city belongs.
const SEPARATORS = /\s*(?:,|\/|\bog\b|\beller\b|\bor\b|\bund\b|&)\s*/iu;

function fold(s) {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ø/gi, 'o')
    .replace(/æ/gi, 'ae')
    .replace(/å/gi, 'a')
    .replace(/ß/gi, 'ss')
    .toLowerCase()
    .trim();
}

/**
 * @param {string} raw value from `location.city`
 * @returns {string} canonical display city, or '' when the value is not a city
 */
export function normaliseCityName(raw) {
  let s = String(raw ?? '').trim();
  if (!s) return '';

  // A leading dash is how a region gets encoded; there is no city behind it.
  if (/^[-–—]/.test(s)) return '';
  // "Ireland Locations", "Germany Locations" — a country rollup, not a place.
  if (/\blocations\s*$/i.test(s)) return '';
  // "US -", "Sweden -", "India -" — a truncated region.
  if (/[-–—]\s*$/.test(s)) return '';

  s = s.replace(WFH_TAIL, '').replace(OFFICE_TAIL, '').replace(BARE_TAIL, '').trim();
  if (!s) return '';

  // A list of places: take the first. Only 1% of rows, and a single canonical
  // column cannot represent "either of these" without becoming an array —
  // which is a bigger change than the filter needs. Recorded as a limitation.
  const [first] = s.split(SEPARATORS).map((p) => p.trim()).filter(Boolean);
  if (!first) return '';
  s = first;

  // A street address that leaked into the field: "Mehringdamm 51",
  // "Stuttgart Schockenriedstr. 17". Reject rather than strip the number —
  // stripping leaves the STREET as the city, which is a worse lie than a blank,
  // because it looks like a real place and would get its own filter row.
  if (/\b\d{1,4}\s*$/.test(s)) return '';

  s = s.replace(DK_DISTRICT, '').trim();
  if (!s) return '';

  const key = fold(s);
  if (!key || NOT_A_CITY.has(key)) return '';
  // A bare country code or a one-letter remnant is not a place.
  if (key.length < 2) return '';

  const alias = ALIASES.get(key);
  if (alias) return alias;

  // Unknown city: title-case it so two sources shouting differently
  // ("OSLO" vs "Oslo") still land on one row.
  return s
    .toLocaleLowerCase()
    .replace(/(^|[\s\-'])(\p{L})/gu, (_, sep, ch) => sep + ch.toLocaleUpperCase());
}
